import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Button } from 'grommet';
import Case from 'case';
import { SimpleModal } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductOfferingType } from '@RedflagAI/node-utility/interface';
import { StyledServiceOptionContainer } from '../StyledOnboardingComponents';

const SOCIAL_PLATFORM_OPTIONS = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'dailymotion', label: 'Dailymotion' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'twitter', label: 'X' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'blogspot', label: 'Blogspot' },
  { id: 'quora', label: 'Quora' },
  { id: 'tumblr', label: 'Tumblr' },
  { id: 'disqus', label: 'Disqus' },
  { id: 'vk', label: 'VK' },
];

const getPlatformLabel = (id) => {
  const found = SOCIAL_PLATFORM_OPTIONS.find((p) => p.id === id);
  return found ? found.label : Case.title(id);
};

const UPLOADER_LABEL = {
  self: 'Self-managed',
  redflag: 'Managed by Redflag',
};

/* eslint-disable camelcase */
const FREQUENCY_LABEL = {
  monthly: 'Monthly',
  one_time: 'One-time',
  oneTime: 'One-time',
};
/* eslint-enable camelcase */

// --- Type resolution ---
function resolveProductType(product) {
  const key = product.productKey || '';
  if (key.startsWith('youtube_content_id')) return ProductOfferingType.CID;
  if (key.startsWith('social_platform_monitoring')) return ProductOfferingType.SOCIAL;
  if (key.startsWith('watermarking')) return ProductOfferingType.WATERMARKING;
  return ProductOfferingType.CUSTOM;
}

// --- Per-product pricing helpers ---
function getProductMonthlyFee(product) {
  const planData = product.plan?.data || {};
  const config = product.config || {};
  const type = resolveProductType(product);

  if (type === ProductOfferingType.CID) {
    return planData.monthlyFeeAmount ?? null;
  }
  if (type === ProductOfferingType.SOCIAL) {
    const platforms = config.platforms || [];
    const defaultAmount = planData.defaultPerPlatformAmount ?? 0;
    const overrides = planData.platformOverrides || [];
    const overrideMap = new Map(overrides.map((o) => [o.platform, o.amount]));
    if (platforms.length === 0) return defaultAmount > 0 ? defaultAmount : null;
    return platforms.reduce(
      (sum, p) => sum + (overrideMap.has(p) ? overrideMap.get(p) : defaultAmount),
      0,
    );
  }
  // watermarking / custom
  const freq = planData.frequency;
  const isMonthly = freq === 'monthly';
  return isMonthly ? (planData.amount ?? null) : null;
}

function getProductSetupFee(product) {
  const planData = product.plan?.data || {};
  const config = product.config || {};
  const type = resolveProductType(product);

  if (type === ProductOfferingType.CID) {
    if (planData.setupFeeAmount == null) return null;
    const channels = config.channels || [];
    return planData.setupFeeAmount * (channels.length || 1);
  }
  return planData.setupFee ?? null;
}

// --- Summary box (matches PricingStep renderSummaryBox) ---

const SummaryBox = ({ title, amount, highlight = false }) => {
  const { theme: { page, button } } = useAppTheme();
  return (
    <Box
      flex="grow"
      background={highlight ? button.selectBg : page.cardBg}
      round="var(--app-radius)"
      pad="1rem"
      align="start"
      justify="start"
      gap="0.5rem"
    >
      <Text
        size="1rem"
        weight={700}
        color={highlight ? page.altHighlight : page.secondaryText}
      >
        {title}
      </Text>
      <Text size="1.35rem" weight={700} color={page.primaryText}>
        {`$${amount}.00`}
      </Text>
    </Box>
  );
};

SummaryBox.propTypes = {
  title: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  highlight: PropTypes.bool,
};

// --- Per-product detail card (matches PricingStep ServiceSummaryContainer style) ---

const ProductCard = ({ small, product }) => {
  const { theme: { page, button } } = useAppTheme();
  const [showChannelsModal, setShowChannelsModal] = React.useState(false);
  const [showPlatformsModal, setShowPlatformsModal] = React.useState(false);

  const { productLabel, plan, config } = product;
  const planData = plan?.data || {};
  const productType = resolveProductType(product);

  const fieldRow = (label, value) => (
    <Box direction="row" gap="0.25rem">
      <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>
        {`${label}:`}
      </Text>
      <Text color={page.primaryText} weight={600} size="1rem">
        {value}
      </Text>
    </Box>
  );

  const renderCidFields = () => {
    const channels = config?.channels || [];
    const uploader = config?.uploader || planData.uploader;
    return (
      <Box gap="0.5rem" width="100%">
        {uploader && fieldRow('Asset uploader', UPLOADER_LABEL[uploader] || uploader)}
        <Box direction="row" gap="0.25rem" align="center">
          <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>
            YouTube channels:
          </Text>
          {channels.length > 0 ? (
            <Button
              plain
              onClick={() => setShowChannelsModal(true)}
              label={(
                <Text
                  color={button.primaryBg}
                  weight={600}
                  size="1rem"
                  style={{ textDecoration: 'underline' }}
                >
                  {`View (${channels.length})`}
                </Text>
              )}
            />
          ) : (
            <Text color={page.secondaryText} size="1rem">Not provided</Text>
          )}
        </Box>
        {planData.setupFeeAmount != null && fieldRow(
          'Setup fee',
          channels.length > 0
            ? `$${planData.setupFeeAmount * channels.length}.00 (${channels.length} channel${channels.length !== 1 ? 's' : ''})`
            : `$${planData.setupFeeAmount}.00 per channel`,
        )}
        {planData.monthlyFeeAmount != null && fieldRow('Monthly fee', `$${planData.monthlyFeeAmount}.00`)}
        {planData.revenueSharePercent != null && fieldRow(
          'Revenue share',
          `${planData.revenueSharePercent}%${planData.revenueShareStartMonth > 0 ? ` (from month ${planData.revenueShareStartMonth})` : ''}`,
        )}
        {showChannelsModal && (
          <SimpleModal
            small={small}
            toggleModal={setShowChannelsModal}
            modalTitle={`YouTube Channels (${channels.length})`}
            highlightColor={button.primaryBg}
            hideLowerSection
            desktopMinWidth={small ? '96vw' : 'min(90vw, 32rem)'}
          >
            <Box
              width="100%"
              overflow={{ vertical: 'auto', horizontal: 'hidden' }}
              gap="0.5rem"
              style={{ maxHeight: '60vh' }}
            >
              {channels.map((ch) => (
                <Box
                  key={ch}
                  width="100%"
                  pad={{ vertical: '0.75rem', horizontal: '0.75rem' }}
                  round="var(--app-radius)"
                  background={button.selectBg}
                >
                  <Text
                    size="0.85rem"
                    color={page.primaryText}
                    style={{ wordBreak: 'break-all', overflowWrap: 'break-word', display: 'block' }}
                  >
                    {ch}
                  </Text>
                </Box>
              ))}
            </Box>
          </SimpleModal>
        )}
      </Box>
    );
  };

  const renderSocialFields = () => {
    const platforms = config?.platforms || [];
    const mainLink = config?.mainLink;
    const defaultAmount = planData.defaultPerPlatformAmount ?? 0;
    const overrideMap = new Map((planData.platformOverrides || []).map((o) => [o.platform, o.amount]));
    const totalPlatformCost = platforms.reduce(
      (sum, p) => sum + (overrideMap.has(p) ? overrideMap.get(p) : defaultAmount),
      0,
    );
    return (
      <Box gap="0.5rem" width="100%">
        <Box direction="row" gap="0.25rem" align="center">
          <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>
            Platforms:
          </Text>
          {platforms.length > 0 ? (
            <Button
              plain
              onClick={() => setShowPlatformsModal(true)}
              label={(
                <Text
                  color={button.primaryBg}
                  weight={600}
                  size="1rem"
                  style={{ textDecoration: 'underline' }}
                >
                  {`View (${platforms.length})`}
                </Text>
              )}
            />
          ) : (
            <Text color={page.secondaryText} size="1rem">Not specified</Text>
          )}
        </Box>
        {platforms.length > 0 && fieldRow('Total monthly', `$${totalPlatformCost}.00`)}
        {mainLink && fieldRow('Profile link', mainLink)}
        {showPlatformsModal && (
          <SimpleModal
            small={small}
            toggleModal={setShowPlatformsModal}
            modalTitle={`Platforms (${platforms.length})`}
            highlightColor={button.primaryBg}
            hideLowerSection
            desktopMinWidth={small ? '96vw' : 'min(90vw, 28rem)'}
          >
            <Box gap="0.4rem" width="100%">
              {platforms.map((p) => {
                const fee = overrideMap.has(p) ? overrideMap.get(p) : defaultAmount;
                return (
                  <Box
                    key={p}
                    direction="row"
                    align="center"
                    justify="between"
                    pad={{ vertical: '0.65rem', horizontal: '0.75rem' }}
                    round="var(--app-radius)"
                    background={button.selectBg}
                  >
                    <Text size="0.9rem" color={page.primaryText} weight={600}>
                      {getPlatformLabel(p)}
                    </Text>
                    <Text size="0.9rem" color={page.primaryText} weight={600}>
                      {`$${fee}.00/mo`}
                    </Text>
                  </Box>
                );
              })}
            </Box>
          </SimpleModal>
        )}
      </Box>
    );
  };

  const renderWatermarkingOrCustomFields = () => (
    <Box gap="0.5rem" width="100%">
      {planData.setupFee != null && fieldRow('Setup fee', `$${planData.setupFee}.00`)}
      {planData.amount != null && fieldRow('Amount', `$${planData.amount}.00`)}
      {planData.frequency && fieldRow(
        'Billing',
        FREQUENCY_LABEL[planData.frequency] || planData.frequency,
      )}
    </Box>
  );

  const renderFields = () => {
    if (productType === ProductOfferingType.CID) return renderCidFields();
    if (productType === ProductOfferingType.SOCIAL) return renderSocialFields();
    return renderWatermarkingOrCustomFields();
  };

  const isCustom = productType === ProductOfferingType.CUSTOM;
  let displayTitle = productLabel;
  let displaySubtitle = plan?.name ? `Plan: ${plan.name}` : null;
  if (isCustom && plan?.name) {
    displayTitle = plan.name;
    displaySubtitle = plan.description || null;
  }

  return (
    <StyledServiceOptionContainer
      noHover
      style={{ border: 'none' }}
      pad="1rem"
      round="var(--app-radius)"
      width="100%"
      gap="0.5rem"
      active={false}
    >
      <Box direction="row" align="center" justify="between" width="100%">
        <Text size="1.1rem" weight={700} color={page.primaryText}>{displayTitle}</Text>
      </Box>
      {displaySubtitle && (
        <Text size="0.85rem" color={page.secondaryText}>{displaySubtitle}</Text>
      )}
      {renderFields()}
    </StyledServiceOptionContainer>
  );
};

ProductCard.propTypes = {
  small: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    productKey: PropTypes.string,
    productLabel: PropTypes.string.isRequired,
    productType: PropTypes.string,
    plan: PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.object,
      description: PropTypes.string,
    }),
    config: PropTypes.object,
  }).isRequired,
};

// --- Pricing adjustments (extra items) ---

const formatItemValue = (item, currency) => {
  const sign = item.kind === 'discount' ? '-' : '+';
  if (item.valueType === 'percent') return `${sign}${item.value}%`;
  return `${sign}${currency} $${item.value}`;
};

const ExtraItemsSection = ({ items, products, currency }) => {
  const { theme: { page, button } } = useAppTheme();

  if (!items?.length) return null;

  return (
    <Box gap="0.5rem">
      <Text weight={600} size="1rem" color={page.primaryText}>Pricing Adjustments</Text>
      <Box gap="0.5rem">
        {items.map((item, idx) => {
          const isDiscount = item.kind === 'discount';
          const valueStr = formatItemValue(item, currency);
          const freqLabel = item.frequency === 'one_time' ? 'one-time' : 'monthly';
          const linkedProduct = item.productKey
            ? products.find((p) => p.productKey === item.productKey)
            : null;

          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.description}-${idx}`}
              direction="row"
              gap="0.5rem"
              align="center"
              justify="between"
              pad={{ vertical: '0.65rem', horizontal: '0.75rem' }}
              round="var(--app-radius)"
              background={page.cardBg}
              border={{ color: 'var(--button-border)', size: '1px' }}
              wrap
            >
              <Box direction="row" gap="0.4rem" align="center" style={{ minWidth: 0, flex: 1 }}>
                <Box
                  pad={{ vertical: '0.15rem', horizontal: '0.4rem' }}
                  round="0.2rem"
                  background={isDiscount ? '#e8f5e9' : '#fff3e0'}
                  style={{ flexShrink: 0 }}
                >
                  <Text size="0.7rem" weight={700} color={isDiscount ? '#2E7D32' : '#E65100'}>
                    {isDiscount ? 'DISCOUNT' : 'CHARGE'}
                  </Text>
                </Box>
                <Text size="0.9rem" color={page.primaryText} style={{ wordBreak: 'break-word' }}>
                  {item.description}
                </Text>
              </Box>
              <Box direction="row" gap="0.5rem" align="center" style={{ flexShrink: 0 }}>
                <Text
                  size="0.95rem"
                  weight={700}
                  color={isDiscount ? '#2E7D32' : page.primaryText}
                >
                  {valueStr}
                </Text>
                <Box
                  pad={{ vertical: '0.15rem', horizontal: '0.4rem' }}
                  round="0.2rem"
                  background={button.selectBg}
                >
                  <Text size="0.75rem" color={page.secondaryText}>{freqLabel}</Text>
                </Box>
                {linkedProduct && (
                  <Text size="0.8rem" color={page.secondaryText}>
                    {`(${linkedProduct.productLabel})`}
                  </Text>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

ExtraItemsSection.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string.isRequired,
    valueType: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    frequency: PropTypes.string.isRequired,
    productKey: PropTypes.string,
  })),
  products: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
};

// --- Main component ---

const PrefillPricingStep = ({
  small,
  products,
  extraItems,
  currency,
}) => {
  const { theme: { page } } = useAppTheme();

  const allProducts = products || [];

  const cidProducts = allProducts.filter((p) => resolveProductType(p) === ProductOfferingType.CID);
  const socialProducts = allProducts.filter((p) => resolveProductType(p) === ProductOfferingType.SOCIAL);
  const otherProducts = allProducts.filter(
    (p) => resolveProductType(p) !== ProductOfferingType.CID
      && resolveProductType(p) !== ProductOfferingType.SOCIAL,
  );

  const cidMonthly = cidProducts.reduce((s, p) => s + (getProductMonthlyFee(p) ?? 0), 0);
  const socialMonthly = socialProducts.reduce((s, p) => s + (getProductMonthlyFee(p) ?? 0), 0);
  const otherMonthly = otherProducts.reduce((s, p) => s + (getProductMonthlyFee(p) ?? 0), 0);

  const totalSetupFee = allProducts.reduce((s, p) => s + (getProductSetupFee(p) ?? 0), 0);
  const hasSetupFee = allProducts.some((p) => getProductSetupFee(p) != null);

  // Apply amount-type monthly extra items to the estimated total
  const extraMonthlyAdjustment = (extraItems || []).reduce((s, item) => {
    if (item.valueType === 'amount' && item.frequency === 'monthly') {
      return s + (item.kind === 'charge' ? item.value : -item.value);
    }
    return s;
  }, 0);

  const estimatedMonthly = cidMonthly + socialMonthly + otherMonthly + extraMonthlyAdjustment;

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>
          Services & Pricing Summary
        </Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Review the services and pricing included in your onboarding package.
        </Text>
      </Box>
      {/* Summary boxes row — mirrors PricingStep top row */}
      <Box direction={small ? 'column' : 'row'} gap="0.5rem" width="100%" justify="between">
        {hasSetupFee && (
          <SummaryBox title="Setup fee" amount={totalSetupFee} />
        )}
        {cidProducts.length > 0 && (
          <SummaryBox title="Content ID monthly" amount={cidMonthly} />
        )}
        {socialProducts.length > 0 && (
          <SummaryBox title="Social Scanning monthly" amount={socialMonthly} />
        )}
        {otherMonthly > 0 && (
          <SummaryBox title="Other monthly" amount={otherMonthly} />
        )}
        <SummaryBox title="Estimated total per month" amount={estimatedMonthly} highlight />
      </Box>
      {/* Per-product detail cards */}
      {allProducts.length > 0 && (
        <Box gap="1rem" width="100%" background={page.cardBg} pad="1rem" round="0.35rem">
          {allProducts.map((product, idx) => (
            <ProductCard
              key={product.productKey || `product-${idx}`}
              small={small}
              product={product}
            />
          ))}
        </Box>
      )}

      <ExtraItemsSection
        items={extraItems}
        products={allProducts}
        currency={currency || 'USD'}
      />
    </Box>
  );
};

PrefillPricingStep.propTypes = {
  small: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    productKey: PropTypes.string,
    productLabel: PropTypes.string.isRequired,
    productType: PropTypes.string,
    plan: PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.object,
      description: PropTypes.string,
    }),
    config: PropTypes.object,
  })),
  extraItems: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string.isRequired,
    valueType: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    frequency: PropTypes.string.isRequired,
    productKey: PropTypes.string,
  })),
  currency: PropTypes.string,
};

export default PrefillPricingStep;
